<template>
  <div class="animated fadeIn support-admin-page">
    <b-row>
      <b-col cols="12">
        <b-card>
          <template #header>
            <h5 class="m-0">Painel administrativo de suporte</h5>
          </template>
          <b-card-body>
            <div class="filters-section">
              <b-form-group label="Pesquisar" class="search-filter">
                <b-input-group>
                  <b-input-group-prepend>
                    <b-input-group-text>
                      <i class="fas fa-search" aria-hidden="true"></i>
                    </b-input-group-text>
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="filters.protocol"
                    type="search"
                    placeholder="Digite para pesquisar protocolo"
                    @keyup.enter="fetchTickets"
                  />
                  <b-input-group-append>
                    <b-button
                      :disabled="!filters.protocol"
                      @click="clearProtocolFilter"
                      >Limpar</b-button
                    >
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>

              <b-form-group label="Status" class="status-filter">
                <b-form-select
                  v-model="filters.status"
                  :options="statusOptions"
                  @change="applyFilters"
                />
              </b-form-group>
            </div>

            <div class="table-container">
              <b-table
                :items="tickets.docs"
                :fields="fields"
                :busy="loading"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :tbody-tr-class="rowClass"
                thead-class="support-header"
                borderless
                hover
                responsive
                show-empty
                no-local-sorting
                @sort-changed="onSortChanged"
                @row-clicked="openTicket"
              >
                <template #cell(protocol)="slotData">
                  <strong class="protocol-link">{{
                    slotData.item.protocol
                  }}</strong>
                </template>
                <template #cell(company)="slotData">
                  <span class="company-name">
                    {{
                      (slotData.item.company &&
                        slotData.item.company.fantasyName) ||
                      (slotData.item.company &&
                        slotData.item.company.businessName) ||
                      "-"
                    }}
                  </span>
                </template>
                <template #cell(requester)="slotData">
                  {{ slotData.item.requester && slotData.item.requester.name }}
                  {{
                    slotData.item.requester && slotData.item.requester.surname
                  }}
                </template>
                <template #cell(status)="slotData">
                  <b-badge :variant="statusVariant(slotData.item.status)">
                    {{ statusLabel(slotData.item.status) }}
                  </b-badge>
                </template>
                <template #cell(createdAt)="slotData">
                  <span class="date-text">{{
                    formatDate(slotData.item.createdAt)
                  }}</span>
                </template>
                <template #cell(resolvedBy)="slotData">
                  <span v-if="slotData.item.resolvedBy">
                    {{ slotData.item.resolvedBy.name }}
                    {{ slotData.item.resolvedBy.surname }}
                  </span>
                  <span v-else>-</span>
                </template>

                <template #table-busy>
                  <div class="text-center py-5">
                    <b-spinner></b-spinner>
                    <span class="ml-2">Carregando...</span>
                  </div>
                </template>
                <template #empty>
                  <div class="text-muted text-center py-5">
                    <i class="fas fa-life-ring fa-2x"></i>
                    <p class="mt-3">Nenhum protocolo encontrado</p>
                  </div>
                </template>
              </b-table>
            </div>

            <nav class="pagination-nav">
              <b-pagination
                v-model="tickets.page"
                :total-rows="tickets.total"
                :per-page="tickets.limit"
                prev-text="Anterior"
                next-text="Próximo"
                align="center"
              />
            </nav>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <b-sidebar
      id="support-ticket-sidebar"
      :visible="sidebarOpen"
      right
      shadow
      backdrop
      no-header-close
      width="75vw"
      @hidden="sidebarOpen = false"
    >
      <template #header>
        <div class="d-flex align-items-center justify-content-between w-100">
          <strong
            >Protocolo {{ selectedTicket && selectedTicket.protocol }}</strong
          >
          <b-button size="sm" variant="link" @click="sidebarOpen = false">
            <i class="fa fa-times"></i>
          </b-button>
        </div>
      </template>

      <div v-if="selectedTicket" class="p-3">
        <p class="mb-1">
          <strong>Empresa:</strong>
          {{
            (selectedTicket.company && selectedTicket.company.fantasyName) ||
            (selectedTicket.company && selectedTicket.company.businessName)
          }}
        </p>
        <p class="mb-1">
          <strong>Data/Hora:</strong> {{ formatDate(selectedTicket.createdAt) }}
        </p>
        <p class="mb-1">
          <strong>Solicitante:</strong>
          {{ selectedTicket.requester && selectedTicket.requester.name }}
          {{ selectedTicket.requester && selectedTicket.requester.surname }}
        </p>
        <p class="mb-1"><strong>Modulo:</strong> {{ selectedTicket.module }}</p>
        <p class="mb-3">
          <strong>Descricao:</strong> {{ selectedTicket.description }}
        </p>

        <b-form-group label="Responsáveis pelo atendimento" class="mb-3">
          <b-dropdown
            block
            variant="outline-secondary"
            toggle-class="assignee-dropdown-toggle"
            menu-class="assignee-dropdown-menu"
            no-caret
            no-close-on-click
            :disabled="savingAssignees || !assigneeUserOptions.length"
          >
            <template #button-content>
              <div class="assignee-dropdown-content">
                <div
                  v-if="selectedAssigneeBadges.length"
                  class="assignee-badges-wrapper"
                >
                  <span
                    v-for="option in selectedAssigneeBadges"
                    :key="option.value"
                    class="assignee-badge"
                  >
                    {{ option.text }}
                  </span>
                </div>
                <span v-else class="assignee-placeholder">
                  Selecione responsáveis
                </span>
                <i class="fa fa-chevron-down assignee-dropdown-icon"></i>
              </div>
            </template>

            <div class="assignee-dropdown-inner">
              <b-form-checkbox-group
                v-model="selectedAssigneeIds"
                :options="assigneeUserOptions"
                value-field="value"
                text-field="text"
                stacked
                :disabled="savingAssignees || !assigneeUserOptions.length"
                class="assignee-dropdown-options"
              />
            </div>
          </b-dropdown>
          <small
            v-if="!assigneeUserOptions.length"
            class="text-muted d-block mt-2"
          >
            Nenhum usuário ativo disponível para designação.
          </small>
          <small class="text-muted d-block mt-1">
            Marque os usuários da empresa que irão tratar este chamado.
          </small>
          <div class="d-flex align-items-center mt-2">
            <b-button
              size="sm"
              variant="primary"
              class="mr-2"
              :disabled="savingAssignees || !hasAssigneeChanges"
              @click="saveAssignees"
            >
              <b-spinner v-if="savingAssignees" small class="mr-1" />
              Salvar designacao
            </b-button>
            <b-button
              size="sm"
              variant="outline-secondary"
              :disabled="savingAssignees || !hasAssigneeChanges"
              @click="resetAssigneesSelection"
            >
              Desfazer
            </b-button>
          </div>
        </b-form-group>

        <div class="timeline-header mt-4">
          <h6 class="m-0">Linha do tempo</h6>
          <b-button
            size="sm"
            variant="primary"
            :disabled="sendingAction || isSelectedTicketConcluded"
            @click="openActionModal"
          >
            <i class="fa fa-plus mr-1"></i>
            Adicionar
          </b-button>
        </div>
        <small v-if="isSelectedTicketConcluded" class="text-muted d-block mb-2">
          Este chamado está concluído. Reabra o atendimento para adicionar novas
          atualizações.
        </small>

        <div class="timeline-list support-timeline">
          <div
            v-for="(message, index) in selectedTicket.messages"
            :key="message._id"
            class="timeline-item"
            :class="{
              'timeline-customer': isCustomerMessage(message, index),
              'timeline-support': !isCustomerMessage(message, index),
            }"
          >
            <div class="timeline-col timeline-left">
              <div
                v-if="isCustomerMessage(message, index)"
                class="timeline-content"
              >
                <div class="timeline-meta">
                  <strong>{{ getTimelineTitle(message, index) }}</strong>
                  <span class="text-muted">{{
                    formatDate(message.createdAt)
                  }}</span>
                </div>
                <div class="timeline-text" v-if="message.text">
                  {{ getTimelineBody(message) }}
                </div>
                <div
                  v-if="message.files && message.files.length"
                  class="timeline-files"
                >
                  <b-button
                    v-for="file in message.files"
                    :key="file._id"
                    size="sm"
                    variant="link"
                    class="pl-0"
                    @click="
                      downloadFile(selectedTicket._id, message._id, file._id)
                    "
                  >
                    <i class="fa fa-paperclip"></i> {{ file.name }}
                  </b-button>
                </div>
              </div>
            </div>

            <div class="timeline-center">
              <div
                class="timeline-node"
                :class="{
                  'timeline-node-support': !isCustomerMessage(message, index),
                }"
              ></div>
            </div>

            <div class="timeline-col timeline-right">
              <div
                v-if="!isCustomerMessage(message, index)"
                class="timeline-content"
              >
                <div class="timeline-meta">
                  <strong>{{ getTimelineTitle(message, index) }}</strong>
                  <span class="text-muted">{{
                    formatDate(message.createdAt)
                  }}</span>
                </div>
                <div class="timeline-text" v-if="message.text">
                  {{ getTimelineBody(message) }}
                </div>
                <div
                  v-if="message.files && message.files.length"
                  class="timeline-files"
                >
                  <b-button
                    v-for="file in message.files"
                    :key="file._id"
                    size="sm"
                    variant="link"
                    class="pl-0"
                    @click="
                      downloadFile(selectedTicket._id, message._id, file._id)
                    "
                  >
                    <i class="fa fa-paperclip"></i> {{ file.name }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-sidebar>

    <b-modal
      id="support-action-modal"
      v-model="showActionModal"
      title="Adicionar na timeline"
      hide-footer
    >
      <b-form @submit.prevent="submitAction">
        <b-form-group label="O que deseja adicionar?">
          <b-form-select
            v-model="actionForm.type"
            :options="availableActionTypeOptions"
            required
          />
        </b-form-group>

        <b-form-group :label="actionTextLabel">
          <b-form-textarea
            v-model="actionForm.text"
            rows="4"
            :placeholder="actionTextPlaceholder"
            required
          />
        </b-form-group>

        <b-form-group label="Anexo (opcional)">
          <b-form-file v-model="actionForm.file" browse-text="Selecionar" />
        </b-form-group>

        <div class="d-flex justify-content-end">
          <b-button
            variant="secondary"
            class="mr-2"
            @click="showActionModal = false"
          >
            Cancelar
          </b-button>
          <b-button type="submit" variant="primary" :disabled="sendingAction">
            <b-spinner v-if="sendingAction" small class="mr-1" />
            Confirmar
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  addSupportMessage,
  escalateSupportTicket,
  getSupportTicket,
  getSupportTickets,
  supportFileDownloadUrl,
  updateSupportStatus,
} from "../../api/support";
import { getCompanyUsers } from "../../api/company";

export default {
  name: "SupportAdmin",
  data() {
    return {
      loading: false,
      sendingAction: false,
      savingAssignees: false,
      sortBy: "createdAt",
      sortDesc: true,
      sidebarOpen: false,
      selectedTicket: null,
      assigneeUserOptions: [],
      selectedAssigneeIds: [],
      initialAssigneeIds: [],
      showActionModal: false,
      actionForm: {
        type: "comment",
        text: "",
        file: null,
      },
      filters: {
        protocol: "",
        status: "",
      },
      statusOptions: [
        { value: "", text: "Todos os status" },
        { value: "open", text: "Aberto" },
        { value: "in_progress", text: "Em atendimento" },
        { value: "waiting_customer", text: "Aguardando cliente" },
        { value: "resolved", text: "Resolvido" },
        { value: "closed", text: "Fechado" },
      ],
      actionTypeOptions: [
        { value: "comment", text: "Comentario" },
        { value: "request_customer", text: "Solicitacao para o cliente" },
        { value: "finalize", text: "Finalizar suporte" },
        {
          value: "escalate_root",
          text: "Solicitar ajuda ao usuario root",
        },
      ],
      fields: [
        { key: "protocol", label: "Protocolo", sortable: true },
        { key: "company", label: "Empresa", sortable: true },
        { key: "requester", label: "Solicitante", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "createdAt", label: "Data da solicitacao", sortable: true },
        { key: "resolvedBy", label: "Resolvido por", sortable: true },
      ],
      tickets: {
        docs: [],
        page: 1,
        limit: 15,
        total: 0,
      },
    };
  },
  watch: {
    "tickets.page"() {
      this.fetchTickets();
    },
  },
  mounted() {
    this.fetchTickets();
  },
  methods: {
    showSuccessToast(message, title = "Sucesso") {
      this.$bvToast.toast(message, {
        title,
        toastClass: "custom-toast custom-toast-success",
        variant: "light",
        solid: true,
        autoHideDelay: 5000,
        noCloseButton: false,
      });
    },
    showErrorToast(message, title = "Erro") {
      this.$bvToast.toast(message, {
        title,
        toastClass: "custom-toast custom-toast-error",
        variant: "light",
        solid: true,
        autoHideDelay: 10000,
        noCloseButton: false,
      });
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString("pt-BR");
    },
    formatShortDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
      });
    },
    statusLabel(status) {
      const labels = {
        open: "Aberto",
        in_progress: "Em atendimento",
        waiting_customer: "Aguardando cliente",
        resolved: "Resolvido",
        closed: "Fechado",
      };
      return labels[status] || status;
    },
    statusVariant(status) {
      const variants = {
        open: "warning",
        in_progress: "primary",
        waiting_customer: "info",
        resolved: "success",
        closed: "secondary",
      };
      return variants[status] || "secondary";
    },
    getTimelineTitle(message, index) {
      if (index === 0) {
        return "Descrição";
      }
      const text = (message && message.text) || "";
      if (text.startsWith("[SOLICITACAO_AJUDA_ROOT]")) {
        return "Solicitacao de ajuda ao root";
      }
      if (text.startsWith("[SOLICITACAO_CLIENTE]")) {
        return "Solicitacao para o cliente";
      }
      if (text.startsWith("[FINALIZACAO]")) {
        return "Finalizacao do suporte";
      }
      return "Comentario";
    },
    getTimelineBody(message) {
      const text = (message && message.text) || "";
      return text
        .replace(/^\[SOLICITACAO_AJUDA_ROOT\]\s*/u, "")
        .replace(/^\[SOLICITACAO_CLIENTE\]\s*/u, "")
        .replace(/^\[FINALIZACAO\]\s*/u, "");
    },
    isCustomerMessage(message, index) {
      if (index === 0) {
        return true;
      }
      return message && message.authorRole !== "support";
    },
    openActionModal() {
      this.actionForm = {
        type: "comment",
        text: "",
        file: null,
      };
      this.showActionModal = true;
    },
    normalizeIdList(values = []) {
      return values
        .map((value) => parseInt(value, 10))
        .filter((value) => Number.isInteger(value) && value > 0);
    },
    sortedIdList(values = []) {
      return [...this.normalizeIdList(values)].sort((a, b) => a - b);
    },
    hydrateAssigneeState(ticket) {
      const assigneeIds = this.normalizeIdList(
        (ticket?.assignees || []).map((assignee) => assignee?._id || assignee)
      );
      this.selectedAssigneeIds = [...assigneeIds];
      this.initialAssigneeIds = this.sortedIdList(assigneeIds);
    },
    async loadAssigneeOptions(ticket) {
      const companyId = parseInt(ticket?.company?._id || ticket?.company, 10);
      if (!Number.isInteger(companyId) || companyId <= 0) {
        this.assigneeUserOptions = [];
        this.selectedAssigneeIds = [];
        this.initialAssigneeIds = [];
        return;
      }

      const response = await getCompanyUsers(companyId);
      this.assigneeUserOptions = (response.data || []).map((user) => ({
        value: Number(user._id),
        text: `${user.name || ""} ${user.surname || ""}`.trim(),
      }));
      this.hydrateAssigneeState(ticket);
    },
    resetAssigneesSelection() {
      this.selectedAssigneeIds = [...this.initialAssigneeIds];
    },
    async saveAssignees() {
      if (!this.selectedTicket || !this.hasAssigneeChanges) {
        return;
      }

      this.savingAssignees = true;
      try {
        const response = await updateSupportStatus(this.selectedTicket._id, {
          status: this.selectedTicket.status,
          assignees: this.normalizeIdList(this.selectedAssigneeIds),
        });
        this.selectedTicket = response.data;
        this.hydrateAssigneeState(this.selectedTicket);
        await this.fetchTickets();
        this.showSuccessToast("Responsáveis atualizados com sucesso");
      } catch (error) {
        const message =
          (error && error.response && error.response.data) ||
          "Erro ao salvar designacao de responsaveis";
        this.showErrorToast(message);
      } finally {
        this.savingAssignees = false;
      }
    },
    rowClass(item) {
      if (!item) return "";
      const baseClass = "support-row";
      return item.status === "closed"
        ? `${baseClass} support-concluded`
        : baseClass;
    },
    onSortChanged(sortOptions) {
      if (sortOptions.sortBy) {
        this.sortBy = sortOptions.sortBy;
        this.sortDesc = sortOptions.sortDesc || false;
      } else {
        this.sortBy = null;
        this.sortDesc = false;
      }
      this.fetchTickets();
    },
    applyFilters() {
      this.tickets.page = 1;
      this.fetchTickets();
    },
    clearProtocolFilter() {
      this.filters.protocol = "";
      this.applyFilters();
    },
    async fetchTickets() {
      this.loading = true;
      try {
        const response = await getSupportTickets({
          page: this.tickets.page,
          size: this.tickets.limit,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
          protocol: this.filters.protocol || undefined,
          status: this.filters.status || undefined,
        });

        this.tickets = {
          ...this.tickets,
          ...response.data,
        };
      } catch (error) {
        this.showErrorToast("Erro ao carregar chamados de suporte");
      } finally {
        this.loading = false;
      }
    },
    async updateStatusIfNeeded(status) {
      if (!this.selectedTicket || this.selectedTicket.status === status) {
        return;
      }
      const response = await updateSupportStatus(this.selectedTicket._id, {
        status,
      });
      this.selectedTicket = response.data;
    },
    async submitAction() {
      if (!this.selectedTicket) return;
      if (this.isSelectedTicketConcluded) {
        this.showErrorToast(
          "Chamado concluído. Reabra o atendimento para adicionar novas mensagens."
        );
        return;
      }

      this.sendingAction = true;
      try {
        let messageText = (this.actionForm.text || "").trim();
        if (!messageText) {
          this.showErrorToast("Preencha o texto da acao");
          return;
        }

        if (this.actionForm.type === "request_customer") {
          messageText = `[SOLICITACAO_CLIENTE] ${messageText}`;
        }
        if (this.actionForm.type === "finalize") {
          messageText = `[FINALIZACAO] ${messageText}`;
        }

        if (this.actionForm.type === "escalate_root") {
          const response = await escalateSupportTicket(
            this.selectedTicket._id,
            {
              reason: messageText,
            }
          );
          this.selectedTicket = response.data;
          await this.fetchTickets();
          this.showActionModal = false;
          this.showSuccessToast("Solicitacao de ajuda enviada para o root");
          return;
        }

        let payload = { text: messageText };
        if (this.actionForm.file) {
          payload = new FormData();
          payload.append("text", messageText);
          payload.append("file", this.actionForm.file);
        }

        const response = await addSupportMessage(
          this.selectedTicket._id,
          payload
        );
        this.selectedTicket = response.data;

        if (this.actionForm.type === "request_customer") {
          await this.updateStatusIfNeeded("waiting_customer");
        }

        if (this.actionForm.type === "finalize") {
          await this.updateStatusIfNeeded("resolved");
        }

        await this.fetchTickets();
        this.showActionModal = false;
        this.showSuccessToast("Atualizacao adicionada com sucesso");
      } catch (error) {
        const message =
          (error && error.response && error.response.data) ||
          "Erro ao adicionar na timeline";
        this.showErrorToast(message);
      } finally {
        this.sendingAction = false;
      }
    },
    async openTicket(ticket) {
      try {
        const response = await getSupportTicket(ticket._id);
        this.selectedTicket = response.data;
        await this.loadAssigneeOptions(this.selectedTicket);
        this.sidebarOpen = true;
      } catch (error) {
        const message =
          (error && error.response && error.response.data) ||
          "Erro ao abrir protocolo";
        this.showErrorToast(message);
      }
    },
    downloadFile(ticketId, messageId, fileId) {
      window.open(
        supportFileDownloadUrl(ticketId, messageId, fileId),
        "_blank"
      );
    },
  },
  computed: {
    ...mapGetters(["profile"]),
    isRoot() {
      return this.profile && this.profile.role === "root";
    },
    availableActionTypeOptions() {
      if (this.isRoot) {
        return this.actionTypeOptions.filter(
          (option) => option.value !== "escalate_root"
        );
      }
      return this.actionTypeOptions;
    },
    actionTextLabel() {
      if (this.actionForm.type === "request_customer") {
        return "Solicitacao para o cliente";
      }
      if (this.actionForm.type === "finalize") {
        return "O que foi resolvido";
      }
      if (this.actionForm.type === "escalate_root") {
        return "Descreva a ajuda necessaria";
      }
      return "Comentario";
    },
    actionTextPlaceholder() {
      if (this.actionForm.type === "request_customer") {
        return "Descreva o que o cliente precisa enviar ou responder";
      }
      if (this.actionForm.type === "finalize") {
        return "Descreva brevemente o que foi resolvido";
      }
      if (this.actionForm.type === "escalate_root") {
        return "Explique o contexto para acionar o atendimento root";
      }
      return "Digite o comentario interno";
    },
    hasAssigneeChanges() {
      const current = this.sortedIdList(this.selectedAssigneeIds);
      const initial = this.sortedIdList(this.initialAssigneeIds);
      if (current.length !== initial.length) {
        return true;
      }
      return current.some((value, index) => value !== initial[index]);
    },
    selectedAssigneeBadges() {
      const selectedIds = new Set(
        this.normalizeIdList(this.selectedAssigneeIds)
      );
      return this.assigneeUserOptions.filter((option) =>
        selectedIds.has(Number(option.value))
      );
    },
    isSelectedTicketConcluded() {
      const status = this.selectedTicket && this.selectedTicket.status;
      return status === "resolved" || status === "closed";
    },
  },
};
</script>

<style scoped>
.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.filters-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: flex-end;
}

.search-filter {
  flex: 2;
}

.status-filter {
  flex: 1;
}

.filters-section .form-group {
  margin-bottom: 0;
}

.support-header {
  background-color: #f8f9fa;
}

::v-deep .support-row:hover {
  cursor: pointer;
}

.protocol-link {
  color: #007bff;
  font-weight: 600;
}

.company-name {
  font-weight: 500;
}

.date-text {
  color: #6c757d;
  font-size: 0.875rem;
}

.pagination-nav {
  margin-top: 1.5rem;
  display: flex;
  justify-content: center;
}

.timeline-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.assignee-dropdown-toggle {
  width: 100%;
  min-height: 40px;
  text-align: left;
  border-color: #d8dee6;
  background: #fff;
}

.assignee-dropdown-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.assignee-badges-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  flex: 1;
}

.assignee-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background: #e8f3ff;
  color: #1f3f62;
  border: 1px solid #b8d7ff;
  font-size: 0.82rem;
}

.assignee-placeholder {
  color: #6c757d;
}

.assignee-dropdown-icon {
  color: #6c757d;
  flex-shrink: 0;
}

::v-deep .assignee-dropdown-menu {
  width: 100%;
  max-width: none;
}

.assignee-dropdown-inner {
  max-height: 220px;
  overflow-y: auto;
  padding: 0.35rem 0.75rem;
}

.assignee-dropdown-options {
  min-width: 260px;
}

.timeline-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.support-timeline {
  position: relative;
}

.support-timeline::before {
  content: "";
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #d6e3f9;
  transform: translateX(-50%);
}

.timeline-item {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 28px minmax(0, 1fr);
  column-gap: 0.75rem;
  position: relative;
  align-items: start;
  z-index: 1;
}

.timeline-col {
  min-width: 0;
}

.timeline-left {
  display: flex;
  justify-content: flex-end;
}

.timeline-right {
  display: flex;
  justify-content: flex-start;
}

.timeline-center {
  display: flex;
  justify-content: center;
  padding-top: 1rem;
}

.timeline-content {
  width: 100%;
  max-width: 100%;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.9rem 0.85rem;
  background: #fff;
  min-width: 0;
}

.timeline-customer .timeline-content {
  border-left: 3px solid #17a2b8;
}

.timeline-support .timeline-content {
  border-left: 3px solid #0d6efd;
}

.timeline-node {
  width: 14px;
  height: 14px;
  border: 2px solid #17a2b8;
  border-radius: 50%;
  background: #fff;
}

.timeline-node-support {
  border-color: #0d6efd;
  background: #0d6efd;
}

.timeline-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.45rem;
  gap: 0.5rem;
  align-items: baseline;
}

.timeline-text {
  white-space: pre-wrap;
  line-height: 1.45;
  color: #3f4a57;
  margin-bottom: 0.25rem;
}

::v-deep #support-ticket-sidebar {
  max-width: 1100px;
}
</style>
